import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import PlanDelDiaPrint from './PlanDelDiaPrint'

const CLEAN_TYPES = [
  ['regular', '🧹 Regular'],
  ['deep', '🫧 Deep Clean'],
  ['airbnb', '🏠 Airbnb Turnover'],
  ['moveout', '📦 Move-in/Move-out'],
  ['janitorial', '🏢 Janitorial'],
  ['custom', '✨ Custom'],
]

const COLORS = [
  { bg:'#dbeafe', border:'#3b82f6', text:'#1e40af', label:'Azul' },
  { bg:'#dcfce7', border:'#22c55e', text:'#15803d', label:'Verde' },
  { bg:'#fef9c3', border:'#eab308', text:'#854d0e', label:'Amarillo' },
  { bg:'#fce7f3', border:'#ec4899', text:'#9d174d', label:'Rosa' },
  { bg:'#ede9fe', border:'#8b5cf6', text:'#5b21b6', label:'Morado' },
]

const HOURS = Array.from({length:34},(_,i)=>{
  const totalMin = 5*60 + i*30
  const h = Math.floor(totalMin/60)
  const m = totalMin%60
  const ampm = h<12?'AM':'PM'
  const h12 = h%12||12
  return `${h12}:${m===0?'00':m} ${ampm}`
})

// Converts "9:00 AM" -> minutes since midnight, for chronological sorting.
// Jobs with no time set (all-day) sort to the end.
function timeToMinutes(t) {
  if (!t) return Infinity
  const [hm, ap] = t.split(' ')
  let [h, m] = hm.split(':').map(Number)
  if (ap === 'PM' && h !== 12) h += 12
  if (ap === 'AM' && h === 12) h = 0
  return h * 60 + (m || 0)
}

export default function PlanDelDia({ date, clients, staff, onClose, onSaved }) {
  // Persist draft to Supabase + localStorage on every teams change
  const draftKey = `plan_draft_${date}`
  const [step, setStep] = useState(1)
  const [numTeams, setNumTeams] = useState(1)
  const [activeTeam, setActiveTeam] = useState(0)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [scheduledJobs, setScheduledJobs] = useState([]) // already on calendar
  const [compatMap, setCompatMap] = useState({}) // {clientId: {staffId: level}}
  // "Really" scheduled — excludes overdue/skipped so those don't keep haunting the plan
  const activeScheduledJobs = scheduledJobs.filter(j => j.status !== 'overdue' && j.status !== 'skipped')

  // teams: [{staffIds:[], jobs:[{clientId, time, hours, clientRate, teamFlat, teamPayType, notes, search, isNew}]}]
  const [teams, setTeams] = useState([{staffIds:[], jobs:[], drivers:[]}])
  const [showPrint, setShowPrint] = useState(false)
  // Draft only autosaves after a real edit. Opening/loading the plan never writes.
  const hydrated = useRef(false)
  const baseline = useRef('')

  const dateLabel = new Date(date+'T12:00:00').toLocaleDateString('es-MX',{weekday:'long',month:'long',day:'numeric'})

  // Save draft to localStorage + Supabase whenever teams change (any step)
  useEffect(()=>{
    if (!hydrated.current) return
    if (JSON.stringify(teams) === baseline.current) return // nothing edited, don't overwrite
    if (teams.some(t=>t.jobs.length>0||t.staffIds.length>0)) {
      const draftData = JSON.stringify({teams, numTeams, step})
      // Supabase only, so every device sees the same draft
      supabase.from('routes').upsert({
        route_date: date,
        label: '__draft__',
        color: '#bdc1c6',
        notes: draftData,
      }, { onConflict: 'route_date,label' }).then(()=>{})
    }
  },[teams, numTeams, step])

  useEffect(()=>{
    hydrated.current = false
    // Old per-device drafts caused stale routes to overwrite the real plan. Wipe them.
    try { Object.keys(localStorage).filter(k=>k.startsWith('plan_draft_')).forEach(k=>localStorage.removeItem(k)) } catch(e) {}
    async function load() {
      const [{data:jb},{data:cp},{data:routes}] = await Promise.all([
        supabase.from('jobs').select('*').eq('scheduled_date',date),
        supabase.from('client_staff_compat').select('*'),
        supabase.from('routes').select('*').eq('route_date',date),
      ])
      const jobs = jb||[]
      setScheduledJobs(jobs)
      const cm = {}
      ;(cp||[]).forEach(r=>{ if(!cm[r.client_id])cm[r.client_id]={}; cm[r.client_id][r.staff_id]=r.level })
      setCompatMap(cm)

      // Shared Supabase draft only (no device-local fallback)
      try {
        const { data: supabaseDraft } = await supabase.from('routes').select('notes').eq('route_date', date).eq('label', '__draft__').maybeSingle()
        const draftStr = supabaseDraft?.notes
        const draft = draftStr ? JSON.parse(draftStr) : null
        if (draft && clients.length > 0) {
          const parsed = draft
          if (parsed?.teams?.length > 0) {
            const enriched = parsed.teams.map(t=>({
              ...t,
              jobs: t.jobs.map(j=>({
                ...j,
                search: j.search || clients.find(cl=>cl.id===j.clientId)?.nickname || clients.find(cl=>cl.id===j.clientId)?.name || ''
              }))
            }))
            baseline.current = JSON.stringify(enriched)
            setTeams(enriched)
            setNumTeams(enriched.length)
            setStep(parsed.step || 2)
            hydrated.current = true
            return
          }
        }
      } catch(e) {}

      // Fallback: load from DB jobs
      const byRoute = {}
      jobs.filter(j=>j.route&&j.status!=='skipped'&&j.status!=='overdue').forEach(job=>{
        const r = job.route
        if (!byRoute[r]) byRoute[r] = []
        byRoute[r].push(job)
      })
      const routeKeys = Object.keys(byRoute).sort()
      if (routeKeys.length > 0) {
        const preTeams = routeKeys.map(rk => {
          const routeJobs = byRoute[rk]
          const routeRow = (routes||[]).find(r=>r.label===rk)
          let staffIds = []
          let drivers = []
          try { 
            const parsed = routeRow ? JSON.parse(routeRow.notes||'{}') : {}
            staffIds = parsed.staffIds||[]
            drivers = parsed.drivers||[]
          } catch(e) {}
          return {
            staffIds,
            drivers,
            jobs: routeJobs.map(j=>{
              const client = clients.find(cl=>cl.id===j.client_id)
              return {
                clientId: j.client_id,
                time: j.start_time||'',
                hours: j.hours||2,
                clientRate: j.flat_rate||'',
                teamFlat: '',
                teamPayType: 'hours',
                notes: j.one_time_note||'',
                cleanType: j.clean_type || (client?.is_airbnb ? 'airbnb' : client?.is_realtor ? 'deep' : 'regular'),
                skipWalkthrough: j.skip_walkthrough || false,
                specialTimeNote: j.special_time_note || '',
                customChecklist: j.custom_checklist || [],
                search: client?.nickname||client?.name||'',
                isNew: false,
              }
            })
          }
        })
        baseline.current = JSON.stringify(preTeams)
        setTeams(preTeams)
        setNumTeams(preTeams.length)
        setStep(2)
      } else {
        baseline.current = JSON.stringify([{staffIds:[], jobs:[], drivers:[]}])
      }
      hydrated.current = true
    }
    load()
  },[date, clients.length])

  function initTeams(n) {
    setNumTeams(n)
    setTeams(Array.from({length:n},(_,i)=>teams[i]||{staffIds:[],jobs:[],drivers:[]}))
    setStep(2)
  }

  function updateTeam(ti, key, val) {
    setTeams(prev=>prev.map((t,i)=>i===ti?{...t,[key]:val}:t))
  }

  function toggleStaff(ti, staffId) {
    const cur = teams[ti].staffIds
    const updated = cur.includes(staffId) ? cur.filter(s=>s!==staffId) : [...cur,staffId]
    // Also remove from drivers if unassigned
    const curDrivers = teams[ti].drivers||[]
    const updatedDrivers = updated.includes(staffId) ? curDrivers : curDrivers.filter(d=>d!==staffId)
    setTeams(prev=>prev.map((t,i)=>i===ti?{...t,staffIds:updated,drivers:updatedDrivers}:t))
  }

  function toggleDriver(ti, staffId) {
    const cur = teams[ti].drivers||[]
    const updated = cur.includes(staffId) ? cur.filter(d=>d!==staffId) : [...cur,staffId]
    setTeams(prev=>prev.map((t,i)=>i===ti?{...t,drivers:updated}:t))
  }

  function addJob(ti, client, isNew) {
    const existing = scheduledJobs.find(j=>j.client_id===client.id)
    const job = {
      clientId: client.id,
      time: existing?.start_time||'9:00 AM',
      hours: existing?.hours||client.default_hours||2,
      clientRate: existing?.flat_rate||client.default_rate||'',
      teamFlat: '',
      teamPayType: 'hours', // 'hours'|'flat'
      notes: existing?.one_time_note||client.notes||'',
      cleanType: existing?.clean_type || (client.is_airbnb ? 'airbnb' : client.is_realtor ? 'deep' : 'regular'),
      skipWalkthrough: existing?.skip_walkthrough || false,
      specialTimeNote: existing?.special_time_note || '',
      customChecklist: existing?.custom_checklist || [],
      search: '',
      isNew,
    }
    setTeams(prev=>prev.map((t,i)=>i===ti?{...t,jobs:[...t.jobs,job]}:t))
  }

  function addJobFromSearch(ti, client) {
    const alreadyScheduled = activeScheduledJobs.some(j=>j.client_id===client.id)
    addJob(ti, client, !alreadyScheduled)
  }

  function updateJob(ti, ji, key, val) {
    setTeams(prev=>prev.map((t,i)=>i===ti?{...t,jobs:t.jobs.map((j,jj)=>jj===ji?{...j,[key]:val}:j)}:t))
  }

  function removeJob(ti, ji) {
    setTeams(prev=>prev.map((t,i)=>i===ti?{...t,jobs:t.jobs.filter((_,jj)=>jj!==ji)}:t))
  }

  // Get compatibility warnings for a team
  function getWarnings(ti) {
    const team = teams[ti]
    const warnings = []
    team.jobs.forEach(job=>{
      const client = clients.find(c=>c.id===job.clientId)
      if (!client) return
      team.staffIds.forEach(sid=>{
        const level = compatMap[job.clientId]?.[sid]
        const s = staff.find(x=>x.id===sid)
        if (level==='dislike') warnings.push(`⚠️ ${client.nickname||client.name} no le gusta trabajar con ${s?.name}`)
        if (level==='only') {
          // check if the "only" person is assigned
          const onlyIds = Object.entries(compatMap[job.clientId]||{}).filter(([,v])=>v==='only').map(([k])=>k)
          const hasOnly = onlyIds.some(id=>team.staffIds.includes(id))
          if (!hasOnly) warnings.push(`⚠️ ${client.nickname||client.name} solo acepta empleadas específicas`)
        }
      })
    })
    // Same person on two teams at overlapping times: the staff clock-in app can
    // only show one, so she'd end up at the wrong house.
    const range = j => {
      const st = timeToMinutes(j.time)
      if (st === Infinity) return null
      return [st, st + (parseFloat(j.hours) || 2) * 60]
    }
    const label = j => {
      const c = clients.find(x=>x.id===j.clientId)
      return `${c?.nickname||c?.name||'?'} ${j.time}`
    }
    team.staffIds.forEach(sid=>{
      teams.forEach((other, oi)=>{
        if (oi===ti || !other.staffIds.includes(sid)) return
        team.jobs.forEach(a=>{
          const ra = range(a); if (!ra || !a.clientId) return
          other.jobs.forEach(b=>{
            const rb = range(b); if (!rb || !b.clientId) return
            if (ra[0] < rb[1] && rb[0] < ra[1]) {
              const s = staff.find(x=>x.id===sid)
              warnings.push(`🚫 ${s?.name} está en Equipo ${ti+1} y Equipo ${oi+1} a la misma hora: ${label(a)} (Eq ${ti+1}) y ${label(b)} (Eq ${oi+1})`)
            }
          })
        })
      })
    })
    return [...new Set(warnings)]
  }

  async function saveOrSubmit(submit) {
    if (saving) return // guard against double-clicks firing this twice
    setSaving(true)

    // Fetch a FRESH snapshot right now — never trust scheduledJobs from initial
    // load, since it goes stale the moment you save once in this session
    // (this was the cause of duplicate inserts when Guardar/Publicar got hit more than once)
    const { data: freshJobs } = await supabase.from('jobs').select('*').eq('scheduled_date', date)
    const current = freshJobs || []
    // Only a truly active job counts as "already exists" — an old overdue/skipped
    // row for this client shouldn't block a fresh insert or get silently reused
    const activeCurrent = current.filter(j => j.status !== 'overdue' && j.status !== 'skipped')

    // If a job was on today's plan before but got removed (×'d out of every team),
    // don't delete it — just clear its route/time so it goes back to unscheduled
    // for the day, instead of disappearing entirely.
    const keptClientIds = new Set(teams.flatMap(t => t.jobs.filter(j=>j.clientId).map(j=>j.clientId)))
    const toUnassign = activeCurrent.filter(j => !keptClientIds.has(j.client_id))
    for (const job of toUnassign) {
      await supabase.from('jobs').update({ route: null, start_time: null }).eq('id', job.id)
    }

    for (const team of teams) {
      for (const job of team.jobs) {
        if (!job.clientId) continue
        const existing = activeCurrent.find(j=>j.client_id===job.clientId)
        if (existing) {
          // Update existing job (and make sure it's actually marked scheduled —
          // in case this client had been previously overdue/skipped and is being
          // intentionally brought back into today's plan)
          await supabase.from('jobs').update({
            start_time: job.time||null,
            hours: parseFloat(job.hours)||2,
            flat_rate: job.clientRate ? parseFloat(job.clientRate) : null,
            one_time_note: job.notes||'',
            route: String(teams.indexOf(team)+1),
            status: 'scheduled',
            clean_type: job.cleanType || 'regular',
            skip_walkthrough: !!job.skipWalkthrough,
            special_time_note: job.specialTimeNote||null,
            custom_checklist: job.cleanType==='custom' ? (job.customChecklist||[]).filter(x=>x.trim()) : null,
          }).eq('id', existing.id)
        } else {
          // Insert new job
          await supabase.from('jobs').insert([{
            client_id: job.clientId,
            scheduled_date: date,
            start_time: job.time||null,
            hours: parseFloat(job.hours)||2,
            status: 'scheduled',
            route: String(teams.indexOf(team)+1),
            one_time_note: job.notes||'',
            week_key: 'plan-'+date,
            color_idx: teams.indexOf(team),
            flat_rate: job.clientRate ? parseFloat(job.clientRate) : null,
            clean_type: job.cleanType || 'regular',
            skip_walkthrough: !!job.skipWalkthrough,
            special_time_note: job.specialTimeNote||null,
            custom_checklist: job.cleanType==='custom' ? (job.customChecklist||[]).filter(x=>x.trim()) : null,
          }])
        }
      }
      // Save route staff assignments always (save + submit)
      const routeLabel = String(teams.indexOf(team)+1)
      const existingRoute = await supabase.from('routes').select('*').eq('route_date',date).eq('label',routeLabel).maybeSingle()
      const staffNote = JSON.stringify({staffIds: team.staffIds, drivers: team.drivers||[], note: '', draft: !submit})
      if (existingRoute.data) {
        await supabase.from('routes').update({notes: staffNote}).eq('id',existingRoute.data.id)
      } else {
        await supabase.from('routes').insert([{route_date:date,label:routeLabel,color:COLORS[teams.indexOf(team)]?.border||'#e8547a',notes:staffNote}])
      }
    }

    setScheduledJobs(current)
    setSaving(false)
    if (submit) {
      // Create payment reminder tasks for each job
      const reminderInserts = []
      teams.forEach((team, ti) => {
        team.jobs.forEach(job => {
          if (!job.clientId) return
          const client = clients.find(c=>c.id===job.clientId)
          if (!client) return
          // Calculate when cleaning ends
          let endTimeStr = ''
          if (job.time) {
            const [hm, ap] = job.time.split(' ')
            const [h, m] = hm.split(':').map(Number)
            let h24 = h; if(ap==='PM'&&h!==12)h24=h+12; if(ap==='AM'&&h===12)h24=0
            const endH24 = h24 + (parseFloat(job.hours)||2)
            const endH = endH24 % 12 || 12
            const endAP = endH24 >= 12 ? 'PM' : 'AM'
            endTimeStr = ` a las ${endH}:00 ${endAP}`
          }
          reminderInserts.push({
            text: `💰 Recordatorio de pago — ${client.nickname||client.name} — Limpieza terminada${endTimeStr} el ${date}. Equipo ${ti+1}. Rate: $${job.clientRate||client.default_rate||'?'}`,
            done: false,
            urgency: 'normal',
            assigned_to: 'Yosi',
            due_date: date,
          })
        })
      })
      if (reminderInserts.length > 0) {
        await supabase.from('todos').insert(reminderInserts)
      }
      try { localStorage.removeItem(draftKey) } catch(e) {}
      // Clear Supabase draft too
      await supabase.from('routes').delete().eq('route_date', date).eq('label', '__draft__')
      setSubmitted(true); onSaved&&onSaved()
    }
    else setSaved(true)
  }

  // Unscheduled clients (on calendar but not yet added to any team)
  const assignedClientIds = new Set(teams.flatMap(t=>t.jobs.map(j=>j.clientId)).filter(Boolean))
  const scheduledNotAssigned = activeScheduledJobs.filter(j=>!assignedClientIds.has(j.client_id)).map(j=>clients.find(c=>c.id===j.client_id)).filter(Boolean)

  // Step 1
  if (step===1) return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e=>e.stopPropagation()}>
        <div style={s.head}>
          <div>
            <div style={s.title}>📋 Plan del día</div>
            <div style={s.sub}>{dateLabel}</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={{padding:'20px 24px'}}>
          <div style={{fontSize:14,fontWeight:600,color:'#202124',marginBottom:8}}>¿Cuántos equipos hoy?</div>
          <div style={{fontSize:12,color:'#5f6368',marginBottom:16,lineHeight:1.7,background:'#f8f9fa',padding:'10px 14px',borderRadius:8,border:'1px solid #e3e5e8'}}>
            💡 Incluye todos los equipos — incluyendo si Mami va sola. Cada equipo = un grupo que va a las casas. El equipo de las oficinas y del apartamento yo lo voy a añadir si es Maisa y la Abuela. Si va a ser alguien más tú añádelos a sus rutas please para que yo no lo toque.
          </div>
          {activeScheduledJobs.length>0&&(
            <div style={{fontSize:12,color:'#185fa5',marginBottom:12,padding:'8px 12px',background:'#e8f0fe',borderRadius:8}}>
              📅 Hay {activeScheduledJobs.length} cliente{activeScheduledJobs.length>1?'s':''} ya en el horario para este día
            </div>
          )}
          <div style={{display:'flex',gap:10}}>
            {[1,2,3,4].map(n=>(
              <button key={n} onClick={()=>initTeams(n)}
                style={{flex:1,padding:'18px 8px',border:'2px solid #e3e5e8',borderRadius:12,background:'#fff',cursor:'pointer',fontSize:22,fontWeight:700,color:'#e8547a',display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                {n}
                <span style={{fontSize:10,color:'#9aa0a6',fontWeight:400}}>equipo{n>1?'s':''}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  // Step 2: Form
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{...s.modal,width:'92vw',maxWidth:1020,maxHeight:'92vh',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
        <div style={s.head}>
          <div>
            <div style={s.title}>📋 Plan del día — {dateLabel}</div>
            {submitted&&<div style={{fontSize:11,color:'#1d9e75',marginTop:2}}>✓ Publicado — horario y rutas actualizados</div>}
            {saved&&!submitted&&<div style={{fontSize:11,color:'#185fa5',marginTop:2}}>💾 Borrador guardado</div>}
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Team tabs + add/remove */}
        <div style={{display:'flex',borderBottom:'1px solid #e3e5e8',flexShrink:0,overflowX:'auto',alignItems:'stretch'}}>
          {teams.map((_,ti)=>{
            const warns = getWarnings(ti)
            return (
              <button key={ti} onClick={()=>setActiveTeam(ti)}
                style={{padding:'10px 18px',border:'none',borderBottom:`3px solid ${activeTeam===ti?COLORS[ti].border:'transparent'}`,background:'transparent',cursor:'pointer',fontSize:13,fontWeight:activeTeam===ti?600:400,color:activeTeam===ti?COLORS[ti].text:'#80868b',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                <div style={{width:10,height:10,borderRadius:'50%',background:COLORS[ti].bg,border:`2px solid ${COLORS[ti].border}`}}/>
                Equipo {ti+1}
                <span style={{fontSize:10,color:'#9aa0a6'}}>({teams[ti].jobs.length})</span>
                {warns.length>0&&<span style={{fontSize:10,color:'#e8547a'}}>⚠️</span>}
              </button>
            )
          })}
          {/* Add/remove team buttons */}
          <div style={{display:'flex',alignItems:'center',gap:4,padding:'0 10px',marginLeft:'auto',flexShrink:0}}>
            {teams.length > 1 && (
              <button onClick={()=>{
                const updated = teams.slice(0,-1)
                setTeams(updated)
                setNumTeams(updated.length)
                if(activeTeam>=updated.length) setActiveTeam(updated.length-1)
              }} style={{fontSize:12,padding:'3px 8px',border:'1px solid #e3e5e8',borderRadius:6,background:'#fff',color:'#80868b',cursor:'pointer'}}>
                − Equipo
              </button>
            )}
            {teams.length < 4 && (
              <button onClick={()=>{
                const updated=[...teams,{staffIds:[],jobs:[]}]
                setTeams(updated)
                setNumTeams(updated.length)
                setActiveTeam(updated.length-1)
              }} style={{fontSize:12,padding:'3px 8px',border:'1px solid #e8547a',borderRadius:6,background:'#fdeef3',color:'#e8547a',cursor:'pointer',fontWeight:500}}>
                + Equipo
              </button>
            )}
          </div>
        </div>

        <div style={{display:'flex',flex:1,overflow:'hidden'}}>
          {/* LEFT: Form */}
          <div style={{flex:1,overflowY:'auto',padding:'14px'}}>

            {/* Staff assignment */}
            <div style={{marginBottom:14,padding:'10px 12px',background:COLORS[activeTeam].bg,borderRadius:10,border:`1px solid ${COLORS[activeTeam].border}`}}>
              <div style={{fontSize:11,fontWeight:600,color:COLORS[activeTeam].text,marginBottom:8}}>👩 Empleadas en este equipo</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {staff.filter(s=>s.is_active).map(s=>{
                  const assigned = teams[activeTeam].staffIds.includes(s.id)
                  const isDriving = (teams[activeTeam].drivers||[]).includes(s.id)
                  return (
                    <div key={s.id} style={{display:'flex',alignItems:'center',gap:2}}>
                      <button onClick={()=>toggleStaff(activeTeam,s.id)}
                        style={{fontSize:11,padding:'4px 10px',borderRadius:20,border:`1.5px solid ${assigned?COLORS[activeTeam].border:'#ddd'}`,background:assigned?COLORS[activeTeam].border:'#fff',color:assigned?'#fff':'#5f6368',cursor:'pointer',fontWeight:assigned?500:400}}>
                        {assigned?'✓ ':''}{s.name}
                      </button>
                      {assigned&&(
                        <button onClick={()=>toggleDriver(activeTeam,s.id)}
                          title={isDriving?'Condujo hoy':'¿Condujo hoy?'}
                          style={{fontSize:12,padding:'3px 6px',borderRadius:20,border:`1.5px solid ${isDriving?COLORS[activeTeam].border:'#ddd'}`,background:isDriving?COLORS[activeTeam].bg:'#fff',cursor:'pointer',lineHeight:1}}>
                          🚗
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
              {getWarnings(activeTeam).map((w,i)=>(
                <div key={i} style={{fontSize:11,color:'#e8547a',marginTop:6,padding:'4px 8px',background:'#fdeef3',borderRadius:6}}>{w}</div>
              ))}
            </div>

            {/* Unassigned scheduled clients */}
            {scheduledNotAssigned.length>0&&(
              <div style={{marginBottom:10,padding:'8px 10px',background:'#e8f0fe',borderRadius:8,border:'1px solid #c5ddf5'}}>
                <div style={{fontSize:11,fontWeight:600,color:'#185fa5',marginBottom:6}}>📅 En el horario, sin asignar — toca para agregar</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                  {scheduledNotAssigned.map(c=>(
                    <button key={c.id} onClick={()=>addJob(activeTeam,c,false)}
                      style={{fontSize:11,padding:'3px 10px',borderRadius:20,border:'1px solid #85B7EB',background:'#fff',color:'#0c447c',cursor:'pointer'}}>
                      + {c.nickname||c.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search to add a client not on schedule */}
            <SearchAddClient clients={clients} assignedClientIds={assignedClientIds} onAdd={(c)=>addJobFromSearch(activeTeam,c)} />

            {/* Jobs */}
            {teams[activeTeam].jobs.length===0&&(
              <div style={{textAlign:'center',padding:'28px',color:'#9aa0a6'}}>
                <div style={{fontSize:28,marginBottom:6}}>🏠</div>
                <div style={{fontSize:12}}>Agrega clientes usando los botones de arriba</div>
              </div>
            )}

            {teams[activeTeam].jobs
              .map((job,ji)=>({job,ji}))
              .sort((a,b)=>timeToMinutes(a.job.time)-timeToMinutes(b.job.time))
              .map(({job,ji})=>{
              const col = COLORS[activeTeam]
              const client = clients.find(c=>c.id===job.clientId)
              return (
                <div key={ji} style={{border:`2px solid ${col.border}`,borderRadius:12,padding:'12px',marginBottom:10,background:'#fff'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:col.text}}>{client?.nickname||client?.name}</div>
                      <div style={{fontSize:10,color:'#80868b'}}>{client?.address||''}</div>
                      {job.isNew&&<div style={{fontSize:10,color:'#f59e0b',fontWeight:500}}>⚠️ No estaba en el horario — se agregará como nuevo</div>}
                    </div>
                    <button style={{fontSize:15,border:'none',background:'none',cursor:'pointer',color:'#9aa0a6'}} onClick={()=>removeJob(activeTeam,ji)}>×</button>
                  </div>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
                    <div>
                      <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Hora de inicio</div>
                      <select style={s.inp} value={job.time} onChange={e=>updateJob(activeTeam,ji,'time',e.target.value)}>
                        <option value="">Sin hora (todo el día)</option>
                        {HOURS.map(h=><option key={h}>{h}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Horas de trabajo</div>
                      <input style={s.inp} type="number" min="0.5" max="12" step="0.5"
                        value={job.hours} onChange={e=>updateJob(activeTeam,ji,'hours',e.target.value)}/>
                    </div>
                  </div>

                  <div style={{marginBottom:8}}>
                    <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Tipo de limpieza</div>
                    <select style={s.inp} value={job.cleanType} onChange={e=>updateJob(activeTeam,ji,'cleanType',e.target.value)}>
                      {CLEAN_TYPES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>

                  {job.cleanType==='custom' && (
                    <div style={{background:'#ede9fe',borderRadius:8,padding:'8px 10px',marginBottom:8}}>
                      <div style={{fontSize:10,fontWeight:600,color:'#5b21b6',marginBottom:6}}>✨ Lista personalizada para esta casa (solo esta visita)</div>
                      <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:6}}>
                        {(job.customChecklist||[]).map((item,ii)=>(
                          <div key={ii} style={{display:'flex',alignItems:'center',gap:6}}>
                            <input style={{...s.inp,flex:1}} value={item}
                              onChange={e=>updateJob(activeTeam,ji,'customChecklist',job.customChecklist.map((x,xi)=>xi===ii?e.target.value:x))} />
                            <button style={{fontSize:14,border:'none',background:'none',color:'#9aa0a6',cursor:'pointer'}}
                              onClick={()=>updateJob(activeTeam,ji,'customChecklist',job.customChecklist.filter((_,xi)=>xi!==ii))}>×</button>
                          </div>
                        ))}
                      </div>
                      <button style={{fontSize:11,padding:'5px 10px',border:'1px solid #c4b5fd',borderRadius:8,background:'#fff',color:'#5b21b6',cursor:'pointer',fontWeight:600}}
                        onClick={()=>updateJob(activeTeam,ji,'customChecklist',[...(job.customChecklist||[]),''])}>
                        + Agregar item
                      </button>
                    </div>
                  )}

                  <div style={{background:'#e8f5e9',borderRadius:8,padding:'8px 10px',marginBottom:8}}>
                    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                      <input type="checkbox" checked={job.skipWalkthrough} onChange={e=>updateJob(activeTeam,ji,'skipWalkthrough',e.target.checked)} />
                      <span style={{fontSize:11,fontWeight:600,color:'#1b5e20'}}>Excluir walkthrough — el equipo ya conoce esta casa</span>
                    </label>
                  </div>

                  <div style={{background:'#fff8e1',borderRadius:8,padding:'8px 10px',marginBottom:8}}>
                    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                      <input type="checkbox" checked={!!job.specialTimeNote} onChange={e=>updateJob(activeTeam,ji,'specialTimeNote', e.target.checked ? 'Esta casa es fuera de lo común y necesitará más tiempo de lo usual.' : '')} />
                      <span style={{fontSize:11,fontWeight:600,color:'#854f0b'}}>Fuera de lo común — avisarles algo especial sobre esta visita</span>
                    </label>
                  </div>

                  {/* Client rate */}
                  <div style={{background:'#f8f9fa',borderRadius:8,padding:'8px 10px',marginBottom:8}}>
                    <div style={{fontSize:10,fontWeight:600,color:'#854f0b',marginBottom:6}}>💰 El cliente paga</div>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:11,color:'#5f6368'}}>Flat rate total:</span>
                      <input style={{...s.inp,width:90}} type="number" min="0" placeholder={client?.default_rate||'0'}
                        value={job.clientRate} onChange={e=>updateJob(activeTeam,ji,'clientRate',e.target.value)}/>
                      <span style={{fontSize:11,color:'#80868b'}}>= ${job.clientRate||client?.default_rate||0}</span>
                    </div>
                  </div>

                  {/* Team pay */}
                  <div style={{background:'#e8f5e9',borderRadius:8,padding:'8px 10px',marginBottom:8}}>
                    <div style={{fontSize:10,fontWeight:600,color:'#1b5e20',marginBottom:6}}>👩 El equipo cobra por esta ruta</div>
                    <div style={{display:'flex',gap:6,marginBottom:6}}>
                      {[['hours','Por horas (tarifa normal)'],['flat','Flat rate para el equipo']].map(([v,l])=>(
                        <button key={v} onClick={()=>updateJob(activeTeam,ji,'teamPayType',v)}
                          style={{fontSize:11,padding:'4px 10px',borderRadius:8,border:`1px solid ${job.teamPayType===v?'#22c55e':'#ddd'}`,background:job.teamPayType===v?'#22c55e':'#fff',color:job.teamPayType===v?'#fff':'#5f6368',cursor:'pointer'}}>
                          {l}
                        </button>
                      ))}
                    </div>
                    {job.teamPayType==='flat'&&(
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span style={{fontSize:11,color:'#5f6368'}}>Flat rate equipo:</span>
                        <input style={{...s.inp,width:90}} type="number" min="0" placeholder="0"
                          value={job.teamFlat} onChange={e=>updateJob(activeTeam,ji,'teamFlat',e.target.value)}/>
                      </div>
                    )}
                    {job.teamPayType==='hours'&&<div style={{fontSize:10,color:'#4caf50'}}>Se usará la tarifa normal de cada empleada</div>}
                  </div>

                  {/* Notes */}
                  <div>
                    <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Notas para las empleadas</div>
                    <input style={s.inp} placeholder="Instrucciones especiales..."
                      value={job.notes} onChange={e=>updateJob(activeTeam,ji,'notes',e.target.value)}/>
                  </div>
                </div>
              )
            })}
          </div>

          {/* RIGHT: Mini time grid preview */}
          <div style={{width:280,borderLeft:'1px solid #e3e5e8',background:'#f8f9fa',display:'flex',flexDirection:'column'}}>
            <div style={{padding:'10px 12px',borderBottom:'1px solid #e3e5e8',flexShrink:0}}>
              <div style={{fontSize:11,fontWeight:600,color:'#80868b',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:6}}>Vista previa del día</div>
              <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                {teams.map((_,ti)=>(
                  <button key={ti} onClick={()=>setActiveTeam(ti)}
                    style={{fontSize:10,padding:'2px 8px',borderRadius:6,border:`1px solid ${COLORS[ti].border}`,background:activeTeam===ti?COLORS[ti].border:'#fff',color:activeTeam===ti?'#fff':COLORS[ti].text,cursor:'pointer'}}>
                    E{ti+1}
                  </button>
                ))}
              </div>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'8px'}}>
              {(()=>{
                const startH=5,endH=20,totalH=endH-startH,gridH=470,pxPerH=gridH/totalH
                const timedJobs=teams[activeTeam].jobs.filter(j=>j.clientId&&j.time)
                const allDayJobs=teams[activeTeam].jobs.filter(j=>j.clientId&&!j.time)
                return (<>
                  {allDayJobs.length>0&&(
                    <div style={{marginBottom:6,background:'#fff',borderRadius:6,padding:'4px 6px',border:'1px solid #e3e5e8'}}>
                      <div style={{fontSize:9,color:'#9aa0a6',marginBottom:3}}>Todo el día</div>
                      {allDayJobs.map((job,ji)=>{
                        const client=clients.find(c=>c.id===job.clientId)
                        return <div key={ji} style={{fontSize:10,fontWeight:500,color:COLORS[activeTeam].text,background:COLORS[activeTeam].bg,borderRadius:3,padding:'1px 5px',marginBottom:2}}>{client?.nickname||client?.name}</div>
                      })}
                    </div>
                  )}
                  <div style={{position:'relative',height:gridH,background:'#fff',borderRadius:6,border:'1px solid #e3e5e8',overflow:'hidden'}}>
                    {Array.from({length:totalH+1},(_,i)=>{
                      const h=startH+i
                      return <div key={h} style={{position:'absolute',left:0,right:0,top:i*pxPerH,borderTop:'1px solid #f1f3f4',display:'flex',alignItems:'center'}}>
                        <span style={{fontSize:8,color:'#bdc1c6',paddingLeft:2}}>{h>12?h-12:h}{h>=12?'p':'a'}</span>
                      </div>
                    })}
                    {timedJobs.map((job,ji)=>{
                      const client=clients.find(c=>c.id===job.clientId)
                      const [hm,ap]=job.time.split(' ')
                      const [h,m]=hm.split(':').map(Number)
                      let h24=h; if(ap==='PM'&&h!==12)h24=h+12; if(ap==='AM'&&h===12)h24=0
                      const top=Math.max(0,(h24+m/60-startH)*pxPerH)
                      const height=Math.max(18,(parseFloat(job.hours)||2)*pxPerH)
                      return (
                        <div key={ji} style={{position:'absolute',left:28,right:4,top,height,background:COLORS[activeTeam].bg,borderLeft:`3px solid ${COLORS[activeTeam].border}`,borderRadius:4,padding:'2px 4px',overflow:'hidden'}}>
                          <div style={{fontSize:9,fontWeight:600,color:COLORS[activeTeam].text,lineHeight:1.2}}>{client?.nickname||client?.name}</div>
                          <div style={{fontSize:8,color:COLORS[activeTeam].text,opacity:.7}}>{job.time} · {job.hours}h</div>
                          {client?.entry_code&&<div style={{fontSize:8,color:'#1d9e75'}}>🔑 {client.entry_code}</div>}
                        </div>
                      )
                    })}
                    {timedJobs.length===0&&<div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,color:'#ddd'}}>Sin horas asignadas</div>}
                  </div>
                  <div style={{marginTop:8,padding:'6px 8px',background:'#fff',borderRadius:6,border:'1px solid #e3e5e8'}}>
                    <div style={{fontSize:9,color:'#9aa0a6',marginBottom:3}}>Empleadas</div>
                    {teams[activeTeam].staffIds.length===0
                      ? <div style={{fontSize:10,color:'#ddd'}}>Sin asignar</div>
                      : teams[activeTeam].staffIds.map(sid=>{const s=staff.find(x=>x.id===sid);return <div key={sid} style={{fontSize:10,color:COLORS[activeTeam].text,fontWeight:500}}>{s?.name}</div>})
                    }
                  </div>
                </>)
              })()}
            </div>
          </div>
        </div>

        {/* Overlap warning across all teams, right above Publicar */}
        {(()=>{
          const clashes = [...new Set(teams.flatMap((_,ti)=>getWarnings(ti)).filter(w=>w.startsWith('🚫')))]
          if (!clashes.length) return null
          return (
            <div style={{padding:'8px 16px',background:'#fde8e8',borderTop:'2px solid #dc2626',flexShrink:0}}>
              <div style={{fontSize:12,fontWeight:700,color:'#b91c1c',marginBottom:3}}>⚠️ Choque de horario — la app de reloj solo mostrará una casa</div>
              {clashes.map((w,i)=><div key={i} style={{fontSize:11,color:'#b91c1c'}}>{w}</div>)}
            </div>
          )
        })()}

        {/* Footer */}
        <div style={{padding:'10px 16px',borderTop:'1px solid #e3e5e8',display:'flex',gap:8,justifyContent:'flex-end',background:'#fff',flexShrink:0}}>
          <button style={{padding:'8px 14px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#5f6368',cursor:'pointer',fontSize:12}} onClick={onClose}>Cancelar</button>
          <button style={{padding:'8px 14px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#5f6368',cursor:'pointer',fontSize:12}} onClick={()=>setShowPrint(true)}>🖨️ Imprimir</button>
          <button style={{padding:'8px 14px',border:'1px solid #185fa5',borderRadius:8,background:'#e8f0fe',color:'#185fa5',cursor:'pointer',fontSize:12,fontWeight:500}}
            onClick={()=>saveOrSubmit(false)} disabled={saving}>
            {saving?'...':'💾 Guardar'}
          </button>
          <button style={{padding:'8px 18px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',cursor:'pointer',fontSize:13,fontWeight:600}}
            onClick={()=>saveOrSubmit(true)} disabled={saving}>
            {saving?'...':'✓ Publicar'}
          </button>
        </div>
      </div>

      {showPrint && (
        <PlanDelDiaPrint date={date} teams={teams} clients={clients} staff={staff} onClose={()=>setShowPrint(false)} />
      )}
    </div>
  )
}

function SearchAddClient({ clients, assignedClientIds, onAdd }) {
  const [search, setSearch] = useState('')
  const filtered = search ? clients.filter(c=>!assignedClientIds.has(c.id)&&(c.name+' '+(c.nickname||'')).toLowerCase().includes(search.toLowerCase())) : []
  return (
    <div style={{position:'relative',marginBottom:10}}>
      <input style={{width:'100%',padding:'7px 10px',border:'1px dashed #e3e5e8',borderRadius:8,fontSize:12,background:'#fff'}}
        placeholder="🔍 Buscar cliente para agregar (no en el horario)..."
        value={search} onChange={e=>setSearch(e.target.value)}/>
      {filtered.length>0&&(
        <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid #e3e5e8',borderRadius:8,zIndex:30,maxHeight:140,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}>
          {filtered.slice(0,6).map(c=>(
            <div key={c.id} style={{padding:'7px 12px',fontSize:12,cursor:'pointer',borderBottom:'1px solid #f8f9fa',display:'flex',justifyContent:'space-between'}}
              onClick={()=>{ onAdd(c); setSearch('') }}>
              <span>{c.nickname||c.name}</span>
              {c.default_rate&&<span style={{color:'#9aa0a6'}}>${c.default_rate}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const s = {
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200},
  modal:{background:'#fff',borderRadius:16,boxShadow:'0 16px 48px rgba(0,0,0,0.2)',width:'92vw',maxWidth:960,maxHeight:'90vh',display:'flex',flexDirection:'column'},
  head:{display:'flex',alignItems:'flex-start',justifyContent:'space-between',padding:'14px 18px',borderBottom:'1px solid #e3e5e8',flexShrink:0},
  title:{fontSize:16,fontWeight:700,color:'#202124'},
  sub:{fontSize:12,color:'#80868b',marginTop:2},
  closeBtn:{fontSize:20,border:'none',background:'none',cursor:'pointer',color:'#9aa0a6',lineHeight:1,padding:0},
  inp:{width:'100%',padding:'6px 8px',border:'1px solid #e3e5e8',borderRadius:7,fontSize:12,background:'#f8f9fa',color:'#202124'},
}
